<template>
  <ContentPage title="Privacy Policy" page="privacy" updated="September 15, 2026">
    <p>
      At SnapInsta, we respect your privacy and are committed to being transparent about how our
      website works.
    </p>
    <p>
      This Privacy Policy explains what information may be collected when you use SnapInsta and how
      that information may be used.
    </p>
    <p>
      SnapInsta is an online tool that allows users to download publicly accessible Instagram
      photos, videos, Reels, and Stories by providing a content link.
    </p>

    <h2>Information We Collect</h2>
    <p>
      SnapInsta does not require users to create an account or log in to use our services.
    </p>
    <p>
      We do not intentionally collect or store personal information such as your name, email
      address, phone number, or password through the download tool.
    </p>
    <p>
      When you use our website, our hosting provider, security services, or other technical service
      providers may automatically process limited technical information such as:
    </p>
    <ul>
      <li>IP address</li>
      <li>Browser type and version</li>
      <li>Device type</li>
      <li>Operating system</li>
      <li>Date and time of access</li>
      <li>Referring website</li>
      <li>Basic technical information required to operate and protect the website</li>
    </ul>
    <p>
      This information may be used for security, fraud prevention, troubleshooting, and maintaining
      the availability of our website.
    </p>

    <h2>Instagram Links and Download Requests</h2>
    <p>
      To use SnapInsta, you may paste an Instagram URL into our download tool.
    </p>
    <p>
      We do not require you to submit an Instagram username, password, or Instagram account
      credentials.
    </p>
    <p>
      We do not ask users to provide private Instagram login information.
    </p>
    <p>
      Depending on how the download request is technically processed, the URL you submit may be
      temporarily processed by our systems or third-party technical service providers in order to
      provide the requested download functionality.
    </p>
    <p>
      We do not intentionally build personal profiles from the Instagram URLs submitted through our
      service.
    </p>

    <h2>Cookies</h2>
    <p>
      SnapInsta may use cookies or similar technologies that are necessary for website
      functionality, security, performance, or advertising.
    </p>
    <p>
      At the present time, SnapInsta does not use Google Analytics.
    </p>
    <p>
      If advertising services such as Google AdSense are enabled on the website, third-party
      advertising providers may use cookies, web beacons, IP addresses, or similar technologies to
      deliver, measure, and improve advertising.
    </p>
    <p>
      Google and its advertising partners may use information collected through advertising
      technologies according to their own privacy policies.
    </p>
    <p>
      You can learn more about how Google uses information from sites and apps that use its services
      here:
    </p>
    <p>
      <a
        href="https://policies.google.com/technologies/partner-sites"
        target="_blank"
        rel="noopener noreferrer"
      >
        https://policies.google.com/technologies/partner-sites
      </a>
    </p>

    <h2>Advertising</h2>
    <p>
      SnapInsta may display advertisements provided by third-party advertising networks, including
      Google.
    </p>
    <p>
      Third-party vendors, including Google, may use cookies to serve advertisements based on a
      user's previous visits to this website or other websites.
    </p>
    <p>
      Users may manage or opt out of personalized advertising through Google's advertising settings:
    </p>
    <p>
      <a href="https://adssettings.google.com/" target="_blank" rel="noopener noreferrer">
        https://adssettings.google.com/
      </a>
    </p>
    <p>
      Where required by applicable law, appropriate consent mechanisms may be provided before certain
      advertising cookies or similar technologies are used.
    </p>

    <h2>Third-Party Services</h2>
    <p>
      SnapInsta may use third-party services to provide hosting, security, technical infrastructure,
      advertising, or other website functions.
    </p>
    <p>
      These providers may process limited technical information as necessary to provide their
      services.
    </p>
    <p>
      Third-party services operate under their own privacy policies and terms.
    </p>

    <h2>Data Retention</h2>
    <p>
      SnapInsta does not intentionally maintain user accounts or user profiles.
    </p>
    <p>
      Technical information may be retained by our hosting, security, or service providers for
      periods necessary to operate the service, maintain security, prevent abuse, or comply with
      legal obligations.
    </p>

    <h2>Data Security</h2>
    <p>
      We take reasonable technical and organizational measures to protect our website and the
      information processed through it.
    </p>
    <p>
      However, no internet service can guarantee absolute security.
    </p>

    <h2>Children's Privacy</h2>
    <p>
      SnapInsta is not specifically directed toward children.
    </p>
    <p>
      We do not knowingly collect personal information from children.
    </p>
    <p>
      If you believe that a child has provided personal information to us, please contact us so that
      we can review and take appropriate action.
    </p>

    <h2>Copyright and User Responsibility</h2>
    <p>
      SnapInsta is a technical tool that allows users to process publicly accessible content through
      submitted URLs.
    </p>
    <p>
      Users are responsible for ensuring that their use of downloaded content complies with
      applicable laws, copyright rules, Instagram's terms, and the rights of content owners.
    </p>
    <p>
      SnapInsta does not claim ownership of content downloaded through the service.
    </p>

    <h2>Changes to This Privacy Policy</h2>
    <p>
      We may update this Privacy Policy from time to time to reflect changes to our services,
      technologies, advertising partners, or legal requirements.
    </p>
    <p>
      Any changes will be posted on this page with an updated "Last Updated" date.
    </p>

    <h2>Contact Us</h2>
    <p>
      If you have questions about this Privacy Policy or our privacy practices, please contact us
      through our <router-link to="/contact/">Contact Us</router-link> page.
    </p>
  </ContentPage>
</template>

<script>
import ContentPage from '@/components/ContentPage.vue';

export default {
  name: 'PrivacyPolicy',
  components: {
    ContentPage,
  },
};
</script>
